import React, { useState, useEffect } from 'react';
import { Plus, Edit3, ArrowLeft, Loader2, AlertCircle, RefreshCw, Calendar, Clock, Eye, Bookmark } from 'lucide-react';
import { useLazyGetAllServiceQuery } from '../../../redux/actions/Admin/ServiceAdmin';
import { 
  useLazyGetPackageByServiceIdQuery, 
  useUpdatePackageMutation 
} from '../../../redux/actions/Admin/ServiceAdmin';
import { IserviceResponse } from '../../../types/Response/AdminResponse';
import PackageFormScreen from './PackageFormScreen';

export default function ManagePackages() {

  const [triggerGetAllServices, {
    data: services = [],
    isLoading: servicesLoading,
    isError: servicesError,
  }] = useLazyGetAllServiceQuery();

  const [triggerGetPackages, { data: packagesData, isLoading: packagesLoading }] = useLazyGetPackageByServiceIdQuery();
  const [updatePackage] = useUpdatePackageMutation();

  const [view, setView] = useState<'services-select' | 'packages-list' | 'package-form'>('services-select');
  const [selectedService, setSelectedService] = useState<IserviceResponse | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');

  useEffect(() => {
    triggerGetAllServices();
  }, [triggerGetAllServices]);

  const currentPackages = Array.isArray(packagesData) ? packagesData : packagesData ? [packagesData] : [];

  const handleSelectService = async (service: IserviceResponse) => {
    setSelectedService(service);
    setView('packages-list');
    try {
      await triggerGetPackages(service.id).unwrap();
    } catch (err) {
      console.error("Error retrieving packages:", err);
    }
  };

  const handleTogglePackageActive = async (pkg: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updatePackage({
        ...pkg,
        isActive: !pkg.isActive
      }).unwrap();
    } catch (err) {
      console.error("Failed to update package state:", err);
    }
  };

  const handleAddPackageClick = () => {
    if (!selectedService) return;
    setSelectedPackage(null);
    setFormMode('add');
    setView('package-form');
  };

  const handleEditPackageClick = (pkg: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPackage(pkg);
    setFormMode('edit');
    setView('package-form');
  };

  const sortedServices = [...services].sort((a, b) => a.name.localeCompare(b.name));

  // VIEW 1: SELECT SERVICE
  if (view === 'services-select') {
    return (
      <div className="min-h-screen bg-white px-4 py-6 md:p-8 font-nunito antialiased">
        <div className="max-w-[1400px] mx-auto">
          <div className="border-b border-gray-100 pb-4 md:pb-6 mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Package Management</h1>
            <p className="text-gray-500 mt-1 text-xs md:text-sm font-medium">
              Select a service to view and manage its packages.
            </p>
          </div>

          {servicesLoading ? (
            <div className="flex flex-col items-center justify-center h-[250px]">
              <Loader2 className="w-8 h-8 md:w-10 md:h-10 animate-spin text-green-600" />
              <p className="mt-3 text-xs md:text-sm font-semibold text-gray-400">Loading Services...</p>
            </div>
          ) : servicesError ? (
            <div className="bg-white rounded-2xl p-6 md:p-10 text-center border border-red-100 max-w-md mx-auto shadow-xs">
              <AlertCircle className="w-8 h-8 md:w-10 md:h-10 text-red-500 mx-auto" />
              <h2 className="mt-4 text-base md:text-lg font-bold text-gray-800">Failed to Load Services</h2>
              <button 
                onClick={() => triggerGetAllServices()} 
                className="mt-5 bg-gray-800 text-white px-5 py-2 rounded-xl text-xs md:text-sm font-semibold hover:bg-black transition cursor-pointer inline-flex items-center gap-2"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Try Again
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-x-8 md:gap-y-12 justify-center">
              {sortedServices.map((service) => (
                <div
                  key={service.id}
                  onClick={() => handleSelectService(service)}
                  className="group flex flex-col items-center cursor-pointer transition-all duration-300 relative bg-white p-3 md:p-0 rounded-2xl md:bg-transparent border border-gray-100 md:border-0 shadow-xs md:shadow-none"
                >
                  <div className="relative size-[180px] mx-auto">
                    <div className={`w-full h-full overflow-hidden border-2 md:border-3 transition-all duration-500 bg-gray-50 
                      ${service.isActive 
                        ? 'border-green-500 rounded-[48%_52%_68%_32%_/_42%_48%_52%_58%] group-hover:rounded-[52%_48%_35%_65%_/_48%_55%_45%_52%] group-hover:shadow-xl' 
                        : 'border-gray-200 rounded-[50%] opacity-60'
                      }`}
                    >
                      <img
                        src={service.logoUrl || 'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400'}
                        alt={service.name}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${!service.isActive ? 'grayscale' : ''}`}
                      />
                    </div>
                  </div>
                  <div className="mt-3 md:mt-4 text-center space-y-0.5 md:space-y-1 w-full px-1">
                    <h3 className="text-sm md:text-lg font-bold text-gray-800 group-hover:text-green-600 transition-colors duration-200 line-clamp-1">
                      {service.name}
                    </h3>
                    <span className="text-[10px] md:text-xs font-bold text-gray-400 group-hover:text-gray-700 transition-colors block">
                      Manage Packages &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // VIEW 2: PACKAGES LISTING
  if (view === 'packages-list' && selectedService) {
    return (
      <div className="min-h-screen px-4 py-6 md:p-8">
        <div className="max-w-[1400px] mx-auto space-y-6">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-200 pb-5">
            <div className="space-y-1">
              <button
                onClick={() => { setSelectedService(null); setView('services-select'); }}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400 hover:text-gray-700 transition cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 stroke-[2.5]" />
                Select Different Service
              </button>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                {selectedService.name} <span className="font-medium text-gray-400">/ Packages</span>
              </h1>
            </div>

            <button
              onClick={handleAddPackageClick}
              className="bg-gray-800 hover:bg-black text-white px-5 py-2.5 rounded-xl text-xs md:text-sm font-semibold shadow-xs transition inline-flex items-center justify-center gap-2 cursor-pointer w-full md:w-auto"
            >
              <Plus className="w-4 h-4 stroke-[2.5]" />
              Add Package
            </button>
          </div>

          {packagesLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-7 h-7 md:w-8 md:h-8 text-green-600 animate-spin stroke-[2.5]" />
              <p className="text-xs font-bold text-gray-400 mt-3">Loading Packages...</p>
            </div>
          ) : currentPackages.length > 0 ? (
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              {currentPackages.map((pkg: any, index: number) => {

                return (
                  <div 
                    key={pkg.id || index}
                    className={`rounded-2xl border p-4 md:p-6 shadow-xs flex flex-col justify-between transition-all duration-200 relative ${
                      !pkg.isActive 
                        ? 'bg-gray-50 border-gray-200 opacity-60' 
                        : 'bg-white border-gray-200 text-gray-800' 
                    }`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-gray-100 rounded-md text-gray-600">
                          Package {index + 1}
                        </span>

                        <div className="flex items-center gap-2 md:gap-3 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => handleTogglePackageActive(pkg, e)}
                            className={`w-9 h-5 rounded-full relative transition-all duration-300 cursor-pointer shrink-0 ${
                              pkg.isActive ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-xs transition-transform ${
                              pkg.isActive ? 'translate-x-[18px]' : 'translate-x-[2px]'
                            }`} />
                          </button>

                          <button 
                            onClick={(e) => handleEditPackageClick(pkg, e)}
                            className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <h2 className="text-base md:text-lg font-bold text-gray-900 line-clamp-1">{pkg.packageTitle}</h2>
                        <p className="text-xs font-semibold text-green-600 mt-0.5 line-clamp-1">{pkg.packageSubtitle}</p>
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">{pkg.description}</p>
                      </div>

                      <div className="grid grid-cols-3 gap-2 bg-gray-50 border border-gray-200 rounded-xl p-2.5 text-xs font-semibold text-gray-600">
                        <div className="space-y-0.5 text-center">
                          <span className="block text-[10px] text-gray-400 font-medium">Duration</span>
                          <span className="text-gray-800 flex items-center justify-center gap-1"><Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {pkg.durationMinutes} mins</span>
                        </div>
                        <div className="space-y-0.5 text-center border-l border-gray-200">
                          <span className="block text-[10px] text-gray-400 font-medium">Validity</span>
                          <span className="text-gray-800 flex items-center justify-center gap-1"><Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {pkg.validForMonths} mos</span>
                        </div>
                        <div className="space-y-0.5 text-center border-l border-gray-200">
                          <span className="block text-[10px] text-gray-400 font-medium">Bookings</span>
                          <span className="text-gray-800 flex items-center justify-center gap-1"><Bookmark className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {pkg.includedBookings ?? 0}</span>
                        </div>
                      </div>

                      {pkg.includes && pkg.includes.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-[10px] uppercase font-semibold text-gray-400 block">Includes:</span>
                          <div className="flex flex-wrap gap-1 max-h-[64px] overflow-y-auto pr-1">
                            {pkg.includes.map((inc: string, i: number) => (
                              <span key={i} className="text-xs bg-gray-100 border border-gray-200 text-gray-700 px-2 py-0.5 rounded-md font-medium whitespace-nowrap">
                                • {inc}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-5 pt-3.5 border-t border-gray-200 flex flex-row items-center justify-between gap-2">
                      <div className="space-y-0.5">
                        <span className="text-[10px] uppercase font-semibold text-gray-400 block">Price Starts At</span>
                        {pkg.pricing && pkg.pricing.length > 0 ? (
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className="text-base md:text-lg font-bold text-gray-900">AED {pkg.pricing[0].originalPrice}</span>
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-amber-600">No Price Set</span>
                        )}
                      </div>

                      <button
                        onClick={(e) => handleEditPackageClick(pkg, e)}
                        className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold transition inline-flex items-center gap-1 cursor-pointer shrink-0"
                      >
                        <Eye className="w-3.5 h-3.5 text-gray-400" /> View / Edit
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 md:py-24 text-gray-400 bg-white rounded-2xl border border-dashed border-gray-200 font-medium text-xs md:text-sm px-4">
              No packages found for this service. Click "Add Package" to create one.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <PackageFormScreen 
      mode={formMode}
      serviceId={selectedService!.id}
      initialData={selectedPackage}
      onClose={() => { setView('packages-list'); if (selectedService) triggerGetPackages(selectedService.id); }}
    />
  );
}